/**
 * Which workspace subtree one `wayai pull` / `wayai push` invocation targets —
 * and the refusal when a single invocation names both.
 *
 * `pull` and `push` are the one pair the Data merge neither deleted nor
 * namespaced: one verb each, routing by subtree
 * (docs/reference/rekor-platform-contracts.md §3.2, §3.3). This module owns that
 * decision and nothing else. It resolves a SUBTREE (plus an explicitly-named
 * target when the invocation carried one) and hands off; picking WHICH hub or
 * WHICH base inside the chosen subtree stays with each side's own resolver, so
 * ambiguity within one subtree keeps that side's existing "say which one"
 * message rather than becoming this error.
 *
 * The decision is a pure function of the arguments and the workspace on disk, so
 * the routing matrix and the refusals are testable without a network or a login
 * — which is what makes the contract's "before any network call and before any
 * file write" placement checkable rather than asserted.
 */

import * as path from 'node:path';
import { WAYAI_LAYOUT, resolveLayout, isDirectory, type ResolvedLayout } from './layout.js';
import { sanitizeTerminalText } from './terminal-output.js';
import { WORKSPACE_MANIFEST_LABEL, loadWorkspaceManifest } from './workspace-manifest.js';
import { filterPreviewHubs, scanNewHubs, scanWorkspaceHubs } from './workspace.js';
import { filterEditableBases, isUnder, listBaseFolders } from './base-workspace.js';

export type Subtree = 'hubs' | 'bases';

export interface RepoDefaults {
  /** Folder name or hub id a bare `pull`/`push` targets in the `hubs/` subtree. */
  default_hub?: string;
  /** Folder name or base id a bare `pull`/`push` targets in the `bases/` subtree. */
  default_base?: string;
}

/**
 * Rule 4's repo defaults, from the workspace manifest `wayai-ws/wayai.yaml`
 * (§3.3's layout block, `workspace-manifest.ts`).
 *
 * **The soft half of that file's posture split.** Absent, unreadable or
 * malformed all read as "no defaults declared" — a broken manifest must not make
 * `pull` fail before the subtree it would have selected can say anything useful.
 * The org binding sharing the file is read with the opposite posture
 * (`repo-config.ts`), and the two must not leak into each other: an
 * organization this reader cannot resolve is none of its business, and a
 * `default_base` it drops is none of the org reader's.
 *
 * A declared value must be a plain NAME, never a path. This file is committed,
 * so its contents are repo-authored input under the same threat model as the
 * workspace files: a `default_base: ../../../../tmp/x` would otherwise send a
 * bare `wayai pull` to write and prune outside the repo on a teammate's or a CI
 * machine. A value carrying a separator is dropped here rather than refused, so
 * one bad key cannot make every invocation in the repo fail before it can say
 * anything; the selector it would have supplied is simply absent, and the rules
 * below carry on.
 */
export function readRepoDefaults(gitRoot: string): RepoDefaults {
  const load = loadWorkspaceManifest(gitRoot);
  if (load.kind !== 'ok') return {};
  const raw = load.doc;
  const str = (v: unknown) => {
    if (typeof v !== 'string' || !v.trim()) return undefined;
    const value = v.trim();
    if (value.includes('/') || value.includes(path.sep) || value === '.' || value === '..') {
      console.warn(
        `Warning: ignoring ${JSON.stringify(value)} in ${WORKSPACE_MANIFEST_LABEL} — a default names a hub or base, not a path.`,
      );
      return undefined;
    }
    return value;
  };
  return { default_hub: str(raw.default_hub), default_base: str(raw.default_base) };
}

/**
 * The flags whose NEXT argument is a value rather than another token. Listed
 * explicitly (not inferred from a leading `-`) so the positional scan below
 * cannot mistake a flag's value for the invocation's target — a bare word taken
 * as the target would route the whole invocation by it.
 *
 * Covers every value-taking flag `pull`/`push` parse (`--hub`, `--base`,
 * `--label`) plus `--org`, which the Data surface owns and a user may
 * reasonably try here. ADD A FLAG HERE in the same change that teaches
 * `pull`/`push` to take one.
 */
const VALUE_FLAGS = new Set(['--hub', '--base', '--label', '--org']);

interface RoutingTokens {
  hubFlag?: string;
  baseFlag?: string;
  /** Present when `--org` was passed — refused by `routeSubtree`, never honored. */
  orgFlag?: string;
  positional?: string;
}

/**
 * The routing-relevant tokens in an argv tail: the two subtree flags, `--org`,
 * and the first positional.
 *
 * BOTH spellings of a value flag are read — `--base crm` and `--base=crm`. They
 * are the same flag to every user, and the sibling `bases` tree is a commander
 * program, which accepts the `=` form, so the habit arrives here already formed.
 * Matching by exact token alone dropped the whole `--flag=value` at the `-`
 * skip: the `--org` refusal never fired, and an explicit `--base=crm` was
 * silently replaced by whatever rules 3–5 resolved to, including the other
 * subtree.
 */
export function parseRoutingTokens(args: string[]): RoutingTokens {
  const tokens: RoutingTokens = {};
  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    // `--flag=value` is one token; split on the FIRST `=` so a value containing
    // one survives intact.
    const eq = arg.indexOf('=');
    const name = eq === -1 ? arg : arg.slice(0, eq);
    if (VALUE_FLAGS.has(name)) {
      const value = eq === -1 ? args[i + 1] : arg.slice(eq + 1);
      if (value !== undefined) {
        if (name === '--hub') tokens.hubFlag ??= value;
        if (name === '--base') tokens.baseFlag ??= value;
        if (name === '--org') tokens.orgFlag ??= value;
        // Only the space-separated form consumes the NEXT token.
        if (eq === -1) i++;
      }
      continue;
    }
    if (arg.startsWith('-')) continue;
    tokens.positional ??= arg;
  }
  return tokens;
}

/** Where a selector points. `both` and `unknown` are decided by rules further down. */
type SelectorSubtree =
  | { kind: 'hubs' | 'bases'; target: string }
  | { kind: 'both'; target: string }
  | { kind: 'unknown' };

/** First path segment of `p`, or `p` itself when it has none. */
function firstSegment(p: string): string {
  const normalized = p.split(path.sep).join('/');
  const [head] = normalized.split('/').filter(Boolean);
  return head ?? p;
}

/**
 * Resolve which subtree a selector names.
 *
 * Three forms, in order, because they answer for different repos:
 *
 *  1. A **subtree-qualified** selector (`bases/crm`, `wayai-ws/hubs/support`) —
 *     the form the refusal message itself hands the user. It names the subtree
 *     by the canonical subdir NAME, so it works before the folder exists (a
 *     first `pull` materializes it) and in a legacy repo whose hubs actually
 *     live in `workspace/`.
 *  2. A **real path** — resolved against cwd and tested for containment in the
 *     resolved dirs, so `../bases/crm` and an absolute path route too.
 *  3. A **bare name** — decided by which subtree already has a folder of that
 *     name. Present in both is an invocation naming targets in both subtrees:
 *     refusal (a), not a precedence.
 *
 * A bare name matching neither is `unknown`: it does not decide the subtree, and
 * the rules below carry it to whichever subtree they select, where that side's
 * own resolver reports it missing in its own words.
 *
 * Exported for the worktree-scope commands (`wayai use` / `wayai unbind`), which
 * need this one decision — which subtree a selector names — and must NOT get the
 * rest of `routeSubtree`: rules 3–5 (cwd, repo defaults, sole candidate) exist so
 * a BARE `pull`/`push` finds a target, and a scope command that inherited them
 * would silently scope a worktree the user never named. The scope is a guard,
 * never a selector, so it takes the explicit forms only.
 */
export function resolveSelectorSubtree(
  layout: ResolvedLayout | null,
  selector: string,
  cwd: string,
): SelectorSubtree {
  const parts = selector.split(path.sep).join('/').split('/').filter(Boolean);
  const qualified = parts[0] === WAYAI_LAYOUT.wsDir ? parts.slice(1) : parts;
  if (qualified.length > 1) {
    if (qualified[0] === WAYAI_LAYOUT.hubsSubdir) return { kind: 'hubs', target: qualified[1] };
    if (qualified[0] === WAYAI_LAYOUT.basesSubdir) return { kind: 'bases', target: qualified[1] };
  }

  if (!layout) return { kind: 'unknown' };
  const { hubsDir, basesDir } = layout;

  if (selector.includes('/') || selector.includes(path.sep)) {
    const abs = path.resolve(cwd, selector);
    if (isUnder(hubsDir, abs)) return { kind: 'hubs', target: firstSegment(path.relative(hubsDir, abs)) };
    if (isUnder(basesDir, abs)) return { kind: 'bases', target: firstSegment(path.relative(basesDir, abs)) };
    return { kind: 'unknown' };
  }

  const inHubs = isDirectory(path.join(hubsDir, selector));
  const inBases = isDirectory(path.join(basesDir, selector));
  if (inHubs && inBases) return { kind: 'both', target: selector };
  if (inHubs) return { kind: 'hubs', target: selector };
  if (inBases) return { kind: 'bases', target: selector };
  return { kind: 'unknown' };
}

/** A folder that could be a `pull`/`push` target: one either subtree would resolve to. */
interface Candidate {
  subtree: Subtree;
  name: string;
}

/**
 * Rule 5's candidate set: every folder either subtree could resolve to ON ITS
 * OWN.
 *
 * Delegated to each side's own scanner rather than re-derived here. A private
 * scan would have to reproduce what counts as a folder — the legacy `wayai.yaml`
 * identity fallback, the dot-prefix skip, the requirement that the identity file
 * actually parse and carry its id fields, the production-mirror drop — and any
 * divergence produces the worst possible failure for this module: a refusal
 * naming a target the subtree's own resolver does not see, whose two printed
 * recovery commands then both fail.
 *
 * Idless hub folders count too: `resolvePushTarget` treats created and
 * from-scratch folders as co-equal candidates, so a workspace holding one new
 * hub folder and one base folder genuinely spans both subtrees.
 */
function listCandidates(layout: ResolvedLayout): Candidate[] {
  const hubs = [
    ...filterPreviewHubs(scanWorkspaceHubs(layout.hubsDir)).map((h) => h.hubFolder),
    ...scanNewHubs(layout.hubsDir).map((h) => h.hubFolder),
  ];
  const bases = filterEditableBases(listBaseFolders(layout.basesDir)).map((b) => b.folder);
  return [
    ...hubs.map((folder) => ({ subtree: 'hubs' as const, name: path.basename(folder) })),
    ...bases.map((folder) => ({ subtree: 'bases' as const, name: path.basename(folder) })),
  ];
}

/**
 * The refusal, verbatim from §3.3. One text for all three cases: each is an
 * invocation that named a target in each subtree, and what the user needs is the
 * two single-subtree invocations that replace it.
 */
export function mixedInvocationRefusal(verb: string, hubTarget: string, baseTarget: string): string {
  const hub = sanitizeTerminalText(hubTarget);
  const base = sanitizeTerminalText(baseTarget);
  return [
    `Refusing a mixed ${verb}: this invocation spans both subtrees.`,
    `  ${WAYAI_LAYOUT.hubsSubdir}/  -> ${hub}`,
    `  ${WAYAI_LAYOUT.basesSubdir}/ -> ${base}`,
    '',
    'Run one at a time:',
    `  wayai ${verb} ${WAYAI_LAYOUT.hubsSubdir}/${hub}`,
    `  wayai ${verb} ${WAYAI_LAYOUT.basesSubdir}/${base}`,
  ].join('\n');
}

export type RouteResult =
  | { ok: true; subtree: Subtree; selector?: string }
  | { ok: false; refusal: string };

export interface RouteOptions {
  /** `pull` or `push` — the verb the refusal text names. */
  verb: string;
  args: string[];
  gitRoot: string | null;
  cwd?: string;
}

/**
 * Route one invocation to exactly one subtree, or refuse it.
 *
 * §3.3's five rules, in order:
 *
 *  1. an explicit `--hub` / `--base` flag — it names both the subtree and the
 *     target, so it wins over everything below;
 *  2. an explicit positional selector;
 *  3. the folder enclosing the current directory;
 *  4. the repo default from `wayai-ws/wayai.yaml`;
 *  5. the sole candidate folder, counted across BOTH subtrees.
 *
 * and the three refusals — (a) targets named in both subtrees, (b) rules 1–5
 * leaving candidates that span both, (c) both flags passed. A refusal is never a
 * merge and never a silent pick.
 *
 * When every rule is silent — nothing named, cwd outside the workspace, no
 * defaults, no candidate folders anywhere — this returns the `hubs` subtree.
 * §3.3 stops at rule 5 because that state names no target in either subtree, so
 * it is not a mixed invocation; routing to `hubs` keeps a bare `wayai pull` in
 * an empty repo answering with the hub side's existing "pass --hub <uuid> to
 * fetch a hub for the first time", which is the actionable message.
 */
export function routeSubtree(options: RouteOptions): RouteResult {
  const { verb, args, gitRoot } = options;
  const cwd = options.cwd ?? process.cwd();
  const { hubFlag, baseFlag, positional, orgFlag } = parseRoutingTokens(args);

  // `--org` belongs to the `wayai bases …` tree, which publishes it through the
  // commander program's `preAction` hook. These verbs never enter that program,
  // so an accepted `--org` would be silently discarded and the invocation would
  // run against the repo's OWN org — applying, and with `--prune` deleting,
  // against a same-named base in the wrong org while reporting success. Base ids
  // are org-scoped slugs, so a same-named base in both orgs is ordinary. Refused
  // rather than threaded: it is not a documented flag on either verb.
  if (orgFlag !== undefined) {
    return {
      ok: false,
      refusal: [
        `\`wayai ${verb}\` does not take --org: it operates on the organization named by ${WORKSPACE_MANIFEST_LABEL}.`,
        '',
        'To act on another organization:',
        '  wayai bases <command> --org <uuid>   # the Data surface honors it',
        '  # or run from a checkout whose workspace manifest names that organization',
      ].join('\n'),
    };
  }
  // Resolved ONCE: every rule below asks the same layout, and each resolve is
  // three `statSync` calls.
  const layout = gitRoot ? resolveLayout(gitRoot) : null;

  // (c) — both flags. Checked first: it is unambiguous without touching disk.
  if (hubFlag !== undefined && baseFlag !== undefined) {
    return { ok: false, refusal: mixedInvocationRefusal(verb, hubFlag, baseFlag) };
  }

  const fromPositional =
    positional !== undefined ? resolveSelectorSubtree(layout, positional, cwd) : undefined;

  // (a) — a positional naming a folder of that name in each subtree.
  if (fromPositional?.kind === 'both') {
    return {
      ok: false,
      refusal: mixedInvocationRefusal(verb, fromPositional.target, fromPositional.target),
    };
  }

  // Rule 1. The flag wins over the positional — except when the two disagree
  // about the SUBTREE, which is refusal (a) rather than a silent precedence. A
  // positional disagreeing only about the target within the flag's own subtree
  // is the ordinary precedence the rule describes.
  if (hubFlag !== undefined) {
    if (fromPositional?.kind === 'bases') {
      return { ok: false, refusal: mixedInvocationRefusal(verb, hubFlag, fromPositional.target) };
    }
    return { ok: true, subtree: 'hubs', selector: hubFlag };
  }
  if (baseFlag !== undefined) {
    if (fromPositional?.kind === 'hubs') {
      return { ok: false, refusal: mixedInvocationRefusal(verb, fromPositional.target, baseFlag) };
    }
    return { ok: true, subtree: 'bases', selector: baseFlag };
  }

  // Rule 2.
  if (fromPositional?.kind === 'hubs' || fromPositional?.kind === 'bases') {
    return { ok: true, subtree: fromPositional.kind, selector: fromPositional.target };
  }
  // An unresolvable positional decides nothing, but is still the target the user
  // typed — carry it to whichever subtree the rules below select.
  const carried = positional;

  if (!gitRoot || !layout) return { ok: true, subtree: 'hubs', selector: carried };

  // Rule 3. cwd sits under at most one of the two dirs, so this never refuses.
  const { hubsDir, basesDir } = layout;
  if (isUnder(hubsDir, cwd)) return { ok: true, subtree: 'hubs', selector: carried };
  if (isUnder(basesDir, cwd)) return { ok: true, subtree: 'bases', selector: carried };

  // Rule 4. Declaring a default in each subtree leaves two candidates spanning
  // both — refusal (b), the same shape as an ambiguous workspace.
  const defaults = readRepoDefaults(gitRoot);
  if (defaults.default_hub && defaults.default_base) {
    return {
      ok: false,
      refusal: mixedInvocationRefusal(verb, defaults.default_hub, defaults.default_base),
    };
  }
  if (defaults.default_hub) return { ok: true, subtree: 'hubs', selector: carried ?? defaults.default_hub };
  if (defaults.default_base) return { ok: true, subtree: 'bases', selector: carried ?? defaults.default_base };

  // Rule 5.
  const candidates = listCandidates(layout);
  if (candidates.length === 1) {
    return { ok: true, subtree: candidates[0].subtree, selector: carried };
  }
  const hubCandidates = candidates.filter((c) => c.subtree === 'hubs');
  const baseCandidates = candidates.filter((c) => c.subtree === 'bases');
  // (b) — more than one candidate, spanning both subtrees.
  if (hubCandidates.length > 0 && baseCandidates.length > 0) {
    return {
      ok: false,
      refusal: mixedInvocationRefusal(verb, hubCandidates[0].name, baseCandidates[0].name),
    };
  }
  // More than one candidate WITHIN one subtree is that side's own ambiguity: it
  // keeps its existing message, which lists the candidates.
  if (baseCandidates.length > 0) return { ok: true, subtree: 'bases', selector: carried };
  return { ok: true, subtree: 'hubs', selector: carried };
}

/**
 * Route, or print the refusal and exit non-zero.
 *
 * Deliberately prints and exits rather than throwing: a mixed invocation is an
 * expected user condition, not a bug, so it must stay out of the Sentry pipeline
 * — the same posture as the worktree-scope guard. Callers run this FIRST, before
 * auth, before any request and before any file write, which is where §3.3 places
 * the refusal.
 */
export function requireSubtree(
  verb: string,
  args: string[],
  gitRoot: string | null,
): { subtree: Subtree; selector?: string } {
  const result = routeSubtree({ verb, args, gitRoot });
  if (!result.ok) {
    console.error(result.refusal);
    process.exit(1);
  }
  return { subtree: result.subtree, selector: result.selector };
}
